import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PermissionAwarePropertiesPanel } from "./PermissionAwarePropertiesPanel";
import { ComponentSelectionModal } from "./ComponentSelectionModal";
import { ComponentPalette } from "./ComponentPaletteSimple";
import {
  CatalogSourcePicker,
  getCatalogSourceCandidateKey,
} from "./CatalogSourcePicker";
import { DataModal, type DataModalSelection } from "@/components/data/DataModal";
import { TemplateBrowser } from "./TemplateBrowser";
import { LayerTreePanel } from "./LayerTreePanel";
import { FloatingEditorPanel } from "./FloatingEditorPanel";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useRightPanelStore } from "@/stores/editor/right-panel-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { cn } from "@/lib/utils";
import { Database } from "lucide-react";
import type { ComponentDefaultSourceContext } from "@/services/ai/commands/utils/component-defaults";
import { listComponentSchemaSourceCandidates } from "@/services/api/componentSchemaBindingApi";
import {
  buildEmptyPmDatasetSourceTemplate,
  createPmDatasetSource,
} from "@/services/api/pmSourceCreationApi";
import {
  isSelectableCatalogSourceCandidate,
  type CatalogSourceCandidate,
} from "@/types/componentDataBinding";

export const RightSidebar: React.FC = () => {
  const {
    currentPage,
    currentPageId,
    isSelectingComponentForSketch,
    project,
    setSelectingComponentForSketch,
  } = useEditorStore();
  const panelMode = useRightPanelStore((state) => state.mode);
  const addTab = useRightPanelStore((state) => state.addTab);
  const addPanelSourceContext = useRightPanelStore(
    (state) => state.addPanelSourceContext,
  );
  const setMode = useRightPanelStore((state) => state.setMode);
  const setAddTab = useRightPanelStore((state) => state.setAddTab);
  const setAddPanelSourceContext = useRightPanelStore(
    (state) => state.setAddPanelSourceContext,
  );
  const requestingOrganizationId = useOrganizationStore((state) =>
    state.currentContext?.type === "organization"
      ? state.currentContext.id
      : null,
  );
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [catalogCandidates, setCatalogCandidates] = useState<
    CatalogSourceCandidate[]
  >([]);
  const [selectedCatalogCandidateKey, setSelectedCatalogCandidateKey] =
    useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogCreating, setCatalogCreating] = useState(false);
  const [catalogCreateError, setCatalogCreateError] = useState<string | null>(
    null,
  );
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const isCatalogPickerActive = panelMode === "add" && addTab === "components";

  const componentPaletteSourceContext = useMemo<
    ComponentDefaultSourceContext | undefined
  >(() => {
    if (addPanelSourceContext === null) {
      return undefined;
    }

    if (addPanelSourceContext.mode === "static-demo") {
      return addPanelSourceContext;
    }

    return {
      ...addPanelSourceContext,
      projectId: project?.id,
      pageId: currentPage?.id ?? currentPageId,
    };
  }, [addPanelSourceContext, currentPage?.id, currentPageId, project?.id]);

  useEffect(() => {
    if (!isCatalogPickerActive || requestingOrganizationId === null) {
      setCatalogCandidates([]);
      setCatalogLoading(false);
      setCatalogError(null);
      setCatalogCreateError(null);
      return;
    }

    let canceled = false;
    setCatalogLoading(true);
    setCatalogError(null);

    void listComponentSchemaSourceCandidates({
      requestingOrganizationId,
      operation: "read-write",
    })
      .then((response) => {
        if (!canceled) {
          setCatalogCandidates(response.candidates);
        }
      })
      .catch((error: unknown) => {
        if (!canceled) {
          setCatalogCandidates([]);
          setCatalogError(
            error instanceof Error ? error.message : "Could not load sources.",
          );
        }
      })
      .finally(() => {
        if (!canceled) {
          setCatalogLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [isCatalogPickerActive, requestingOrganizationId]);

  useEffect(() => {
    if (selectedCatalogCandidateKey === null) {
      return;
    }

    if (requestingOrganizationId === null) {
      setSelectedCatalogCandidateKey(null);
      setAddPanelSourceContext(null);
      return;
    }

    if (!isCatalogPickerActive || catalogLoading) {
      return;
    }

    const selectedCandidateStillAvailable = catalogCandidates.some(
      (candidate) =>
        getCatalogSourceCandidateKey(candidate) === selectedCatalogCandidateKey,
    );
    if (!selectedCandidateStillAvailable) {
      setSelectedCatalogCandidateKey(null);
      setAddPanelSourceContext(null);
    }
  }, [
    catalogCandidates,
    catalogLoading,
    isCatalogPickerActive,
    requestingOrganizationId,
    selectedCatalogCandidateKey,
    setAddPanelSourceContext,
  ]);

  const handleSelectCatalogCandidate = useCallback(
    (candidate: CatalogSourceCandidate) => {
      if (!isSelectableCatalogSourceCandidate(candidate)) {
        setSelectedCatalogCandidateKey(null);
        setCatalogError("Source is not backed by supported catalog authority.");
        return;
      }

      setCatalogError(null);
      setSelectedCatalogCandidateKey(getCatalogSourceCandidateKey(candidate));
      setAddPanelSourceContext({
        mode: "binding",
        source: candidate.source,
        ...(candidate.capabilities.writable
          ? { writeTarget: candidate.source }
          : {}),
        fieldMapping: {},
      });
    },
    [setAddPanelSourceContext],
  );

  const handleClearCatalogCandidate = useCallback(() => {
    setSelectedCatalogCandidateKey(null);
    setAddPanelSourceContext(null);
  }, [setAddPanelSourceContext]);

  const handleCreatePmSource = useCallback(() => {
    if (requestingOrganizationId === null) {
      setCatalogCreateError(
        "Organization context is required to create a PM source.",
      );
      return;
    }

    setCatalogCreating(true);
    setCatalogCreateError(null);

    void createPmDatasetSource(
      buildEmptyPmDatasetSourceTemplate(),
      requestingOrganizationId,
    )
      .then((result) => {
        if (!isSelectableCatalogSourceCandidate(result.candidate)) {
          setCatalogCreateError(
            "Created PM source is not backed by supported catalog authority.",
          );
          return;
        }

        const createdCandidateKey = getCatalogSourceCandidateKey(
          result.candidate,
        );
        setCatalogCandidates((currentCandidates) => [
          result.candidate,
          ...currentCandidates.filter(
            (candidate) =>
              getCatalogSourceCandidateKey(candidate) !== createdCandidateKey,
          ),
        ]);
        handleSelectCatalogCandidate(result.candidate);
      })
      .catch((error: unknown) => {
        setCatalogCreateError(
          error instanceof Error
            ? error.message
            : "Could not create PM source.",
        );
      })
      .finally(() => {
        setCatalogCreating(false);
      });
  }, [handleSelectCatalogCandidate, requestingOrganizationId]);

  const handleSelectDataModalSource = useCallback(
    (selection: DataModalSelection) => {
      setSelectedCatalogCandidateKey(null);
      setCatalogError(null);

      setAddPanelSourceContext(
        selection.selectionType === "source"
          ? {
              mode: "binding",
              source: selection.source,
              ...(selection.writeTarget === undefined
                ? {}
                : { writeTarget: selection.writeTarget }),
              fieldMapping: {},
            }
          : {
              mode: "binding",
              source: selection.view,
              fieldMapping: {},
            },
      );

      setMode("add");
      setAddTab("components");
    },
    [setAddPanelSourceContext, setAddTab, setMode],
  );

  const handleSelectComponentTypeForSketch = useCallback(() => {
    setShowComponentModal(true);
  }, []);

  const handleCloseComponentModal = useCallback(() => {
    setShowComponentModal(false);
    setSelectingComponentForSketch(false);
  }, [setSelectingComponentForSketch]);

  useEffect(() => {
    if (isSelectingComponentForSketch) {
      setShowComponentModal(true);
    } else {
      setShowComponentModal(false);
    }
  }, [isSelectingComponentForSketch]);

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden bg-background border-l border-border">
        <div className="flex-1 min-h-0 overflow-hidden bg-background">
          <PermissionAwarePropertiesPanel
            onSelectComponentTypeForSketch={handleSelectComponentTypeForSketch}
          />
        </div>
        <ComponentSelectionModal
          open={showComponentModal}
          onClose={handleCloseComponentModal}
        />
      </div>

      <FloatingEditorPanel
        open={panelMode === "add"}
        onClose={() => setMode("chat")}
        title="Add"
        height={520}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center px-3 py-2 border-b gap-1 shrink-0">
            <div
              className="flex items-center flex-1 bg-muted rounded-lg p-1 gap-1"
              role="tablist"
              aria-label="Add panel"
            >
              <TabButton
                active={addTab === "components"}
                onClick={() => setAddTab("components")}
              >
                Components
              </TabButton>
              <TabButton
                active={addTab === "templates"}
                onClick={() => setAddTab("templates")}
              >
                Templates
              </TabButton>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {addTab === "components" ? (
              <div className="h-full min-h-0 flex flex-col">
                <div className="border-b px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setDataModalOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <Database className="h-3.5 w-3.5" />
                    Data
                  </button>
                </div>
                <CatalogSourcePicker
                  candidates={catalogCandidates}
                  selectedCandidateKey={selectedCatalogCandidateKey}
                  isLoading={catalogLoading}
                  error={catalogError}
                  isCreatingPmSource={catalogCreating}
                  createPmSourceError={catalogCreateError}
                  canCreatePmSource={requestingOrganizationId !== null}
                  onCreatePmSource={handleCreatePmSource}
                  onSelectCandidate={handleSelectCatalogCandidate}
                  onClearSelection={handleClearCatalogCandidate}
                />
                <div className="min-h-0 flex-1">
                  <ComponentPalette
                    className="h-full"
                    sourceContext={componentPaletteSourceContext}
                  />
                </div>
              </div>
            ) : (
              <TemplateBrowser className="h-full" />
            )}
          </div>
        </div>
      </FloatingEditorPanel>

      <DataModal
        open={dataModalOpen}
        onOpenChange={setDataModalOpen}
        onSelect={handleSelectDataModalSource}
      />

      <FloatingEditorPanel
        open={panelMode === "layers"}
        onClose={() => setMode("chat")}
        title="Layers"
        height={480}
      >
        <LayerTreePanel />
      </FloatingEditorPanel>
    </>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, children }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      "flex-1 py-1.5 text-sm font-medium rounded-md transition-colors",
      active
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground",
    )}
  >
    {children}
  </button>
);
